import { useTranslation } from 'react-i18next';
import { Text, UnstyledButton } from '@/shared/ui';
import { formatCents } from '@/shared/utils/format-cents';
import PaymentMethodIframe, {
  type PayienFormState,
} from '../PaymentMethodIframe';
import PaymentFormSkeleton from '../PaymentFormSkeleton';
import { CardBrandLogo } from '../SubscriptionPaymentCard';

interface Props {
  /** 'plan' adds the sub-step header: the card is being changed mid-upgrade. */
  mode: 'card' | 'plan';
  formUrl: string | null;
  card: { brand: string; lastFour: string } | null;
  /** Charged the moment the plan change is confirmed; zero in the card mode. */
  chargeTodayCents: number;
  /** What the next renewal will take, for the note under the context card. */
  nextChargeCents: number;
  renewalDate: string;
  daysRemaining: number | null;
  newPlanPrice: string;
  submitSignal: number;
  onStateChange: (state: PayienFormState, message?: string) => void;
  onSuccess: () => void;
  onCancel: () => void;
  onExpired?: () => void;
  /** Back to the review keeping the card already on file. */
  onUseCurrent?: () => void;
}

/**
 * The card form, inside the flow instead of in a modal over it.
 *
 * The host owns the context and the footer: which card is on file, what is
 * being charged right now, and the Save/Cancel buttons. The iframe is left
 * with the fields alone — it never knew what the charge was for, and its own
 * subtitle said "a partir do próximo ciclo", which is false when the card is
 * being replaced in the middle of an upgrade that charges today.
 *
 * @param props - Component props.
 * @returns The card step body.
 */
export function PlanFlowCardStep(props: Props) {
  const { t } = useTranslation('features/pricing');
  const {
    mode, formUrl, card, chargeTodayCents, renewalDate,
    daysRemaining, newPlanPrice, submitSignal, onStateChange, onSuccess,
    onCancel, onExpired, onUseCurrent,
  } = props;

  const midUpgrade = mode === 'plan';

  return (
    <div className="flex flex-col gap-(--space-4)">
      {midUpgrade && (
        <div className="flex items-baseline justify-between gap-(--space-3)">
          <Text component="span" className="[font-weight:var(--font-semibold)]">
            {t('planFlow.card.subtitleUpgrade')}
          </Text>
          {card && onUseCurrent && (
            <UnstyledButton
              onClick={onUseCurrent}
              data-testid="plan-flow-use-current-card"
              className="text-(length:--text-body-sm) text-(--seed-base) underline"
            >
              {t('planFlow.card.useCurrent', { lastFour: card.lastFour })}
            </UnstyledButton>
          )}
        </div>
      )}

      {card && midUpgrade && (
        <div
          data-testid="plan-flow-card-context"
          className="flex flex-col gap-(--space-3) rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-card) p-(--space-4)"
        >
          <div className="flex items-center gap-(--space-3)">
            <CardBrandLogo brand={card.brand} />
            <div className="flex flex-col">
              <Text component="span" className="text-(length:--text-tag) uppercase text-(--text-tertiary)">
                {t('planFlow.card.currentCard')}
              </Text>
              <Text component="span" className="text-(length:--text-body-sm)">
                {`${card.brand} •••• ${card.lastFour}`}
              </Text>
            </div>
            <div className="ms-auto flex flex-col items-end">
              <Text component="span" className="text-(length:--text-tag) uppercase text-(--text-tertiary)">
                {t('planFlow.card.chargedToday')}
              </Text>
              <Text component="span" className="[font-weight:var(--font-bold)]">
                {formatCents(chargeTodayCents)}
              </Text>
            </div>
          </div>
          <Text component="p" className="text-(length:--text-meta) text-(--text-secondary)">
            {t('planFlow.card.upgradeNote', {
              days: daysRemaining ?? 0,
              date: renewalDate,
              price: newPlanPrice,
            })}
          </Text>
        </div>
      )}

      {card && !midUpgrade && (
        <div
          data-testid="plan-flow-card-context"
          className="flex items-center gap-(--space-3) rounded-(--radius-lg) border border-(--border-subtle) p-(--space-4)"
        >
          <CardBrandLogo brand={card.brand} />
          <div className="flex min-w-0 flex-col">
            <Text component="span" className="text-(length:--text-tag) uppercase text-(--text-tertiary)">
              {t('planFlow.card.currentCard')}
            </Text>
            <Text component="span" className="text-(length:--text-body-sm)">
              {`${card.brand} •••• ${card.lastFour}`}
            </Text>
          </div>
        </div>
      )}

      <div>
        {formUrl ? (
          <PaymentMethodIframe
            formUrl={formUrl}
            submitSignal={submitSignal}
            onStateChange={onStateChange}
            onSuccess={onSuccess}
            onCancel={onCancel}
            onExpired={onExpired}
          />
        ) : (
          <PaymentFormSkeleton />
        )}
      </div>
    </div>
  );
}

export default PlanFlowCardStep;
